import classNames from "classnames/bind";
import React, { useState } from "react";
import { Button } from "../../../Button";
import { colours } from "../../../constants/colours";
import { TextDataDisplay } from "../../../DataDisplay/TextDataDisplay";
import { DropdownMenu } from "../../../DropdownMenu";
import { Icon, IconLoading } from "../../../Icon";
import { Skeleton } from "../../../Skeleton";
import { WorkflowNodeProps } from "./types";
import styles from "./WorkflowNode.module.scss";

const cx = classNames.bind(styles);

const WorkflowNode = (props: WorkflowNodeProps) => {
  const {
    icon,
    variant,
    isHovered,
    isSelected,
    title,
    subtitle,
    description,
    isDisabled,
    isLoading,
    isBusy,
    iconTitle,
    isDirty,
    isDummy,
    actions = [],
  } = props;
  const [isActionsMenuOpen, setIsActionsMenuOpen] = useState(false);

  const isDummyTrigger = isDummy && variant === "trigger";

  const stateClassNames = {
    loading: isLoading,
    disabled: !isLoading && isDisabled,
    hover: isHovered, // User can "hover" even when disabled or loading
    selected: !isDisabled && !isLoading && isSelected,
    "is-dummy-trigger": isDummyTrigger,
  };

  if (!variant || !icon) return null;

  return (
    <div className={cx(variant, "outer-container")}>
      <div
        className={cx("dirty-indicator")}
        style={{ opacity: isDirty ? 1 : 0 }}
      />
      <div className={cx("container", stateClassNames)}>
        {isDummyTrigger ? (
          <Icon
            variant="plus-circle"
            size={18}
            colour={isDisabled ? colours.text[200] : colours.text[600]}
            alt={iconTitle}
          />
        ) : (
          <Skeleton
            isLoading={isLoading}
            className={cx("icon-container-skeleton")}
          >
            {isBusy ? (
              <div className={cx("busy-icon-container", stateClassNames)}>
                <IconLoading size={28} />
              </div>
            ) : (
              <div className={cx("icon-container", stateClassNames)}>
                <div
                  className={cx("icon")}
                  title={iconTitle}
                >
                  {icon && (
                    <Icon
                      variant={icon}
                      size={16}
                      alt={iconTitle}
                    />
                  )}
                </div>
              </div>
            )}
          </Skeleton>
        )}
        <div className={cx("information-box", stateClassNames)}>
          <Skeleton
            isLoading={isLoading}
            className={cx("title-skeleton")}
          >
            <TextDataDisplay
              value={title}
              className={cx("title", stateClassNames)}
              clamp={2}
            />
          </Skeleton>
          {subtitle && (
            <Skeleton
              isLoading={isLoading}
              className={cx("subtitle-skeleton")}
            >
              <TextDataDisplay
                value={subtitle}
                className={cx("subtitle", stateClassNames)}
                clamp={1}
              />
            </Skeleton>
          )}
          {description && (
            <Skeleton
              isLoading={isLoading}
              className={cx("description-skeleton")}
            >
              <TextDataDisplay
                value={description}
                className={cx("description", stateClassNames)}
                clamp={2}
              />
            </Skeleton>
          )}
        </div>
        {actions.length > 0 ? (
          <div
            className={cx("actions-container")}
            onClick={(e) => {
              // Prevent clicking the action menu from opening the workflow step drawer
              e.stopPropagation();
            }}
          >
            <Skeleton
              isLoading={isLoading}
              isBusy={isBusy}
            >
              <div
                className={cx(
                  "actions",
                  (isHovered || isActionsMenuOpen) && "always-visible"
                )}
              >
                <DropdownMenu
                  options={actions}
                  rootProps={{ onOpenChange: setIsActionsMenuOpen }}
                >
                  <Button
                    variant="outline"
                    icon="dots-three-vertical"
                    size="xx-small"
                  />
                </DropdownMenu>
              </div>
            </Skeleton>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WorkflowNode;
